import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, TextField, Chip, Typography, Skeleton } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { Warning2 as Warning2IconIx } from "iconsax-react";
import AssigneePicker from "./AssigneePicker";
import ApiIntegrationPanel from "./ApiIntegrationPanel";
import CatalogSendPreview from "../../../../Inbox/Features/TemplateSendFeatures/CatalogSendPreview";
import { fetchCatalogs } from "../../../../../redux/Slice/Catalog/catalogSlice";
import { LabeledField, inboxInputSx } from "./LabeledField";
import ExpandableParagraphField from "./ExpandableParagraphField";
import { getFieldOptionSoftPillSx } from "../../../../../utils/taskTemplateChipStyles";

// Same colourful icon-pill formula as this page's own Knowledge Sources
// pills (Rahim, 2026-09-03: "use pill and icon with cross ... use the type
// of pill we made in config page") — duplicated locally rather than
// imported from AgentConfigView.jsx's sourcePillSx to avoid a circular
// import (AgentConfigView.jsx -> ActionConfigPanel.jsx already).
const ASSIGNEE_TYPE_COLORS = { member: "#3B82F6", team: "#8B5CF6" };
const assigneePillSx = (theme, type) => ({
  ...getFieldOptionSoftPillSx(theme, ASSIGNEE_TYPE_COLORS[type] || ASSIGNEE_TYPE_COLORS.member),
  borderRadius: "8px",
  fontWeight: 600,
  height: 34,
  fontSize: "0.8125rem",
  "& .MuiChip-label": { px: 1.1 },
  "& .MuiChip-icon": { color: "inherit", ml: 1 },
  "& .MuiChip-deleteIcon": { fontSize: "14px", color: "inherit", opacity: 0.6, ml: 0, mr: "6px", "&:hover": { opacity: 1 } },
});

// Context-based routing for the "assignment" action: each route pairs a label (e.g.
// "Sales", "Complaint"), a condition and its own assignee. At runtime the AI picks the
// matching route; the top-level "Assign to" above stays the fallback/default.
function AssignmentRoutes({ routes, onChange }) {
  const updateRoute = (index, patch) =>
    onChange(routes.map((route, i) => (i === index ? { ...route, ...patch } : route)));
  const removeRoute = (index) => onChange(routes.filter((_, i) => i !== index));
  const addRoute = () =>
    onChange([...routes, { label: "", whenToRun: "", assigneeId: "", assigneeType: "", assigneeName: "" }]);

  return (
    <LabeledField label="Routes by context (optional)">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "text.secondary", lineHeight: 1.5 }}>
          Send different kinds of conversations to different teams or members — e.g. sales
          enquiries to Sales, complaints to Support. The AI picks the route that matches the
          conversation. The "Assign to" above is used when no route is chosen.
        </Typography>
        {routes.map((route, index) => (
          <Box
            key={index}
            sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small" fullWidth
                placeholder="Route name (e.g. Sales, Complaint)"
                value={route.label || ""}
                onChange={(e) => updateRoute(index, { label: e.target.value })}
                sx={inboxInputSx}
              />
              <CloseIcon
                onClick={() => removeRoute(index)}
                sx={{ fontSize: 18, cursor: "pointer", color: "text.secondary", "&:hover": { color: "text.primary" } }}
              />
            </Box>
            <ExpandableParagraphField
              label="When to use this route"
              placeholder="e.g. The customer wants pricing, a quote or to buy something…"
              value={route.whenToRun}
              onChange={(v) => updateRoute(index, { whenToRun: v })}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {route.assigneeId ? (
                <Chip
                  icon={route.assigneeType === "team" ? <GroupIcon /> : <PersonIcon />}
                  label={route.assigneeName}
                  onDelete={() => updateRoute(index, { assigneeId: "", assigneeName: "", assigneeType: "" })}
                  deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                  sx={(theme) => assigneePillSx(theme, route.assigneeType)}
                />
              ) : (
                <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>No assignee selected</Typography>
              )}
              <AssigneePicker onSelect={(assignee) => updateRoute(index, assignee)} />
            </Box>
          </Box>
        ))}
        <Typography
          onClick={addRoute}
          sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "primary.main", cursor: "pointer", width: "fit-content" }}
        >
          + Add route
        </Typography>
      </Box>
    </LabeledField>
  );
}

export default function ActionConfigPanel({ configKey, actionConfigs, updateConfig }) {
  const cfg = actionConfigs?.[configKey] || {};
  const dispatch = useDispatch();
  const { catalogs, status: catalogsStatus } = useSelector((state) => state.catalog);

  // Mirrors ChooseActionDrawer's fetch-once guard: only load catalogs when this panel is
  // actually showing the catalog picker, and only if they aren't already loaded/loading.
  useEffect(() => {
    if (configKey !== "catalog_send") return;
    if (catalogsStatus === "loading" || catalogsStatus === "succeeded") return;
    if (catalogs && catalogs.length > 0) return;
    dispatch(fetchCatalogs());
  }, [configKey, catalogsStatus, catalogs, dispatch]);

  // Label-above-box + inboxInputSx (Rahim, 2026-09-03: "for the text boxes
  // use same as we did for the config") — these were plain MUI TextFields
  // with a floating label, the one input style left on this page that
  // hadn't already moved to the Config step's own convention.
  // ExpandableParagraphField (Rahim, 2026-09-04: capped/scrollable +
  // expand-to-fullscreen, rolled out to every genuine free-text box across
  // the AI Agents pages) — these condition/message fields are exactly that
  // shape, not one-liners.
  const whenToRunField = (
    <ExpandableParagraphField
      label="When to run"
      placeholder="Describe the condition that should trigger this action…"
      value={cfg.whenToRun}
      onChange={(v) => updateConfig(configKey, "whenToRun", v)}
    />
  );

  if (configKey === "button_message") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {whenToRunField}
        <ExpandableParagraphField
          label="Body"
          value={cfg.body}
          onChange={(v) => updateConfig(configKey, "body", v)}
        />
        <LabeledField label="Buttons (comma-separated, max 3)">
          <TextField
            size="small" fullWidth
            placeholder="Yes, No, Maybe"
            value={cfg.buttons || ""}
            onChange={(e) => updateConfig(configKey, "buttons", e.target.value)}
            sx={inboxInputSx}
          />
        </LabeledField>
      </Box>
    );
  }

  if (configKey === "catalog_send") {
    const selectedCatalog = cfg.catalogId
      ? { id: cfg.catalogId, name: cfg.catalogName || "", products: Array.isArray(cfg.products) ? cfg.products : [] }
      : null;

    const applyCatalog = (catalog) => {
      updateConfig(configKey, "catalogId", catalog.id || catalog._id || "");
      updateConfig(configKey, "catalogName", catalog.name || "");
      updateConfig(configKey, "products", catalog.products || []);
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {whenToRunField}
        <ExpandableParagraphField
          label="Intro message (optional)"
          placeholder="Message sent before the catalog…"
          value={cfg.message}
          onChange={(v) => updateConfig(configKey, "message", v)}
        />
        {/* Same "!" in a circle + bold text.secondary treatment as the
            image_message caption below (Rahim, 2026-09-03: "u forgot the
            others"). */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <Box sx={{ width: 16, height: 16, flexShrink: 0, mt: "1px", display: "flex", color: "text.secondary" }}>
            <Warning2IconIx size="100%" variant="Bold" />
          </Box>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "text.secondary", lineHeight: 1.5 }}>
            Pick the catalog — at send time the AI searches every product in it against what the
            customer actually asked for and sends only the matches (up to WhatsApp's 30-product
            limit per message). An AI-generated menu intent (or a specific intent's own action
            override) can point at a different catalog — this is only the default.
          </Typography>
        </Box>

        {/* Skeleton rows matching each catalog row's own wrapper shape
            below (borderRadius 1, 1px divider border) instead of plain
            "Loading catalogs…" text (Rahim, 2026-09-04 audit). */}
        {catalogsStatus === "loading" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {[0, 1].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
        )}
        {catalogsStatus !== "loading" && (!catalogs || catalogs.length === 0) && (
          <Typography sx={{ fontSize: "0.75rem", color: "warning.main" }}>
            No catalogs found for this workspace.
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 360, overflowY: "auto" }}>
          {(catalogs || []).map((catalog) => (
            <Box
              key={catalog.id || catalog._id}
              onClick={() => applyCatalog(catalog)}
              sx={{
                borderRadius: 1, border: "1px solid", cursor: "pointer",
                borderColor: cfg.catalogId === (catalog.id || catalog._id) ? "primary.main" : "divider",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <CatalogSendPreview singleCatalog={catalog} />
            </Box>
          ))}
        </Box>
        {selectedCatalog && (
          <Typography sx={{ fontSize: "0.75rem", color: "success.main" }}>
            ✓ Using "{selectedCatalog.name}" ({selectedCatalog.products.length} product
            {selectedCatalog.products.length === 1 ? "" : "s"} available to match against)
          </Typography>
        )}
      </Box>
    );
  }

  if (configKey === "api_integration") {
    return <ApiIntegrationPanel cfg={cfg} updateConfig={updateConfig} />;
  }

  if (configKey === "assignment") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {whenToRunField}
        {/* Was a readonly TextField + separate outlined type-Chip + a
            plain delete IconButton, three elements for one value (Rahim,
            2026-09-03, screenshot: "use pill and icon with cross so the
            user understands it, use the type of pill we made in config
            page") — now one colourful icon pill with a built-in ✕
            (Chip's own onDelete), same formula as Knowledge Sources'
            pills. AssigneePicker's own "@" trigger stays next to it to
            pick/change the assignee. */}
        <LabeledField label="Assign to">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {cfg.assigneeId ? (
              <Chip
                icon={cfg.assigneeType === "team" ? <GroupIcon /> : <PersonIcon />}
                label={cfg.assigneeName}
                onDelete={() => {
                  updateConfig(configKey, "assigneeId", "");
                  updateConfig(configKey, "assigneeName", "");
                  updateConfig(configKey, "assigneeType", "");
                }}
                deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                sx={(theme) => assigneePillSx(theme, cfg.assigneeType)}
              />
            ) : (
              <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>No assignee selected</Typography>
            )}
            <AssigneePicker
              onSelect={({ assigneeId, assigneeName, assigneeType }) => {
                updateConfig(configKey, "assigneeId", assigneeId);
                updateConfig(configKey, "assigneeName", assigneeName);
                updateConfig(configKey, "assigneeType", assigneeType);
              }}
            />
          </Box>
        </LabeledField>
        <AssignmentRoutes
          routes={Array.isArray(cfg.routes) ? cfg.routes : []}
          onChange={(routes) => updateConfig(configKey, "routes", routes)}
        />
      </Box>
    );
  }

  if (configKey === "image_message") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {whenToRunField}
        <ExpandableParagraphField
          label="Caption (optional)"
          placeholder="Text sent alongside the image…"
          value={cfg.caption}
          onChange={(v) => updateConfig(configKey, "caption", v)}
        />
        {/* "!" in a circle (Rahim, 2026-09-03: "use ! in a circle type
            icon") + the original text.secondary tone, bolded instead of
            recoloured (Rahim, next message: "use the same colour it was
            using just bold it dont use this colour" — warning.main from
            the previous pass rejected). alignItems:flex-start since the
            icon should pin to the first line, not center across the full
            3-line paragraph. Font size still dropped a step (0.75->0.6875)
            per "make the paras a little small". */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <Box sx={{ width: 16, height: 16, flexShrink: 0, mt: "1px", display: "flex", color: "text.secondary" }}>
            <Warning2IconIx size="100%" variant="Bold" />
          </Box>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "text.secondary", lineHeight: 1.5 }}>
            There's no image to upload here — at send time the AI picks the image link
            straight out of whatever knowledge source content matched the customer's
            question. Add the image's URL to a knowledge source (e.g. an FAQ answer or a
            product doc) so it's there to find. The AI never generates an image itself.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (configKey === "close_conversation") {
    return <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>{whenToRunField}</Box>;
  }

  return null;
}
